import { api } from '../config/api';

export interface Alert {
  id: number;
  alert_type: 'quantity_mismatch' | 'delivery_overdue' | 'missing_stock_match' | 'container_seal_missing' | 'container_photo_missing' | 'due_diligence_incomplete';
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: 'open' | 'acknowledged' | 'resolved' | 'dismissed';
  po_number: string | null;
  container_id: number | null;
  line_id: number | null;
  title: string;
  description: string;
  expected_value: string | null;
  actual_value: string | null;
  metadata: any;
  assigned_to: string | null;
  created_by: string | null;
  resolved_by: string | null;
  created_at: string;
  acknowledged_at: string | null;
  resolved_at: string | null;
  dismissed_at: string | null;
  resolution_notes: string | null;
  dismissal_reason: string | null;
  container_no?: string | null;
  delivery_day?: string | null;
  days_open?: string;
}

export interface AlertSummary {
  total_open: number;
  total_acknowledged: number;
  total_resolved: number;
  total_dismissed: number;
  critical_count: number;
  high_count: number;
  medium_count: number;
  low_count: number;
  by_type: Record<string, number>;
  oldest_unresolved: {
    id: number;
    title: string;
    created_at: string;
    days_old: string;
  } | null;
}

export interface AlertFilters {
  status?: 'open' | 'acknowledged' | 'resolved' | 'dismissed';
  severity?: 'critical' | 'high' | 'medium' | 'low';
  alert_type?: string;
  po_number?: string;
  assigned_to?: string;
  limit?: number;
}

// Generate alerts
export const generateAlerts = async () => {
  const response = await api.post('/alerts/generate');
  return response.data;
};

// Get alerts with filters
export const getAlerts = async (filters?: AlertFilters) => {
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);
  if (filters?.severity) params.append('severity', filters.severity);
  if (filters?.alert_type) params.append('alert_type', filters.alert_type);
  if (filters?.po_number) params.append('po_number', filters.po_number);
  if (filters?.assigned_to) params.append('assigned_to', filters.assigned_to);
  if (filters?.limit) params.append('limit', filters.limit.toString());

  const response = await api.get(`/alerts?${params.toString()}`);
  return response.data;
};

// Get alerts summary
export const getAlertsSummary = async (): Promise<{ success: boolean; } & AlertSummary> => {
  const response = await api.get('/alerts/summary');
  return response.data;
};

// Acknowledge alert
export const acknowledgeAlert = async (alertId: number, staffNo: string, assignTo?: string) => {
  const response = await api.post(`/alerts/${alertId}/acknowledge`, {
    staff_no: staffNo,
    assigned_to: assignTo
  });
  return response.data;
};

// Resolve alert
export const resolveAlert = async (alertId: number, staffNo: string, resolutionNotes: string) => {
  const response = await api.post(`/alerts/${alertId}/resolve`, {
    staff_no: staffNo,
    resolution_notes: resolutionNotes
  });
  return response.data;
};

// Dismiss alert
export const dismissAlert = async (alertId: number, staffNo: string, dismissalReason: string) => {
  const response = await api.post(`/alerts/${alertId}/dismiss`, {
    staff_no: staffNo,
    dismissal_reason: dismissalReason
  });
  return response.data;
};
